import {
  FormikCompany,
  FormikContractor,
  FormikDocument,
  FormikDriver,
  FormikGreenCard,
  FormikVehicle,
  FormikVehicleDamages,
  FormikVehicleInfo,
  FormikVehicleOwner,
  ImpactPoint,
  VehicleOwnerType,
} from "src/types/formik/formik-vehicle.types";
import {
  CartaVerdeDTO,
  CompagniaDTO,
  ConducenteDTO,
  ContraenteDTO,
  InfoVeicoloDTO,
  ProprietarioDTO,
  TipoPatenteDTO,
  VeicoloDocumentoDTO,
  VeicoloDTO,
} from "../swagger/swagger-vehicle.types";
import { DateMapper, YesNoAnswerMapper } from "./mappers-utils";
import { deleteEmptyKeys } from "src/utils";
import { InputSelectOption } from "src/types/widgets/input-select.types";

export const VehicleMapper = {
  fromDTO(dto: VeicoloDTO): FormikVehicle {
    return {
      owner: OwnerMapper.fromDTO({
        driverDto: dto.proprietario ?? {},
        ownerCoincidesWithContractorDto: dto.conducenteCoincideConContraente,
      }),
      contractor: ContractorMapper.fromDTO(dto.contraente ?? {}),
      vehicleInfo: VehicleInfoMapper.fromDTO(dto.veicolo),
      company: CompanyMapper.fromDTO({
        companyDto: dto.compagnia ?? {},
        policyNumberDto: dto.numeroPolizza ?? "",
        greenCardDto: dto.cartaVerde ?? {},
        materialDamageCoverageDto: dto.coperturaDanniMateriali,
      }),
      vehicleDamages: VehicleDamagesMapper.fromDTO({
        impactPointDto: dto.puntoUrto?.split(",") as ImpactPoint[],
        notesDto: dto.osservazioni,
      }),
      driver: DriverMapper.fromDTO({
        driverDto: dto.conducente,
        driverCoincidesWithContractorDto: dto.conducenteCoincideConContraente,
      }),
    };
  },

  toDTO(form: FormikVehicle): VeicoloDTO {
    return deleteEmptyKeys({
      conducenteCoincideConContraente: YesNoAnswerMapper.toDto(
        form.driver.driverCoincidesWithContractor
      ),
      proprietarioCoincideConContraente: YesNoAnswerMapper.toDto(
        form.owner.ownerCoincidesWithContractor
      ),
      proprietario: OwnerMapper.toDTO(form.owner),
      contraente: ContractorMapper.toDTO(form.contractor),
      veicolo: VehicleInfoMapper.toDTO(form.vehicleInfo),
      numeroPolizza: form.company.policyNumber,
      cartaVerde: GreenCardMapper.toDTO(form.company.greenCard),
      compagnia: CompanyMapper.toDTO(form.company),
      coperturaDanniMateriali: YesNoAnswerMapper.toDto(
        form.company.materialDamageCoverage
      ),
      puntoUrto: form.vehicleDamages.impactPoints.join(","),
      osservazioni: form.vehicleDamages.notes,
      conducente: DriverMapper.toDTO(form.driver),
    });
  },
};

export const OwnerMapper = {
  fromDTO({
    driverDto,
    ownerCoincidesWithContractorDto,
  }: {
    driverDto: ProprietarioDTO;
    ownerCoincidesWithContractorDto?: boolean;
  }): FormikVehicleOwner {
    return {
      surname: driverDto.cognome ?? "",
      name: driverDto.nome ?? "",
      fiscalCodeOrVAT: driverDto.codiceFiscalePartitaIVA ?? "",
      phoneNumber: {
        formattedPhone: driverDto.telefono ?? "",
      },
      email: driverDto.email ?? "",
      address: driverDto.indirizzo ?? "",
      placeOfBirth: driverDto.luogoNascita ?? "",
      dateOfBirth: DateMapper.fromDto(driverDto.dataNascita ?? ""),
      subjectType: driverDto.tipoSoggetto ?? VehicleOwnerType.NATURAL,
      ownerCoincidesWithContractor: YesNoAnswerMapper.fromDto(
        ownerCoincidesWithContractorDto
      ),
    };
  },

  toDTO(form: FormikVehicleOwner): ProprietarioDTO {
    return deleteEmptyKeys({
      cognome: form.surname,
      nome: form.name,
      codiceFiscalePartitaIVA: form.fiscalCodeOrVAT,
      telefono: form.phoneNumber.formattedPhone,
      email: form.email,
      indirizzo: form.address,
      luogoNascita: form.placeOfBirth,
      dataNascita: DateMapper.toDto(form.dateOfBirth),
      tipoSoggetto: form.subjectType,
    });
  },
};

export const ContractorMapper = {
  fromDTO(dto: ContraenteDTO): FormikContractor {
    return {
      name: dto.nome ?? "",
      surname: dto.cognome ?? "",
      fiscalCodeOrVAT: dto.codiceFiscalePartitaIVA ?? "",
      address: dto.indirizzo ?? "",
    };
  },

  toDTO(form: FormikContractor): ContraenteDTO {
    return deleteEmptyKeys({
      nome: form.name,
      cognome: form.surname,
      codiceFiscalePartitaIVA: form.fiscalCodeOrVAT,
      indirizzo: form.address,
    });
  },
};

export const VehicleInfoMapper = {
  fromDTO(dto: InfoVeicoloDTO): FormikVehicleInfo {
    return {
      type: dto.tipo ?? "",
      plate: dto.targa,
      registrationStatus: dto.statoImmatricolazione ?? "",
    };
  },

  toDTO(form: FormikVehicleInfo): InfoVeicoloDTO {
    return deleteEmptyKeys({
      tipo: form.type,
      targa: form.plate,
      statoImmatricolazione: form.registrationStatus,
    });
  },
};

export const GreenCardMapper = {
  fromDTO(dto: CartaVerdeDTO): FormikGreenCard {
    return {
      number: dto.numero ?? "",
      startDate: DateMapper.fromDto(dto.dataInizio ?? ""),
      endDate: DateMapper.fromDto(dto.dataFine ?? ""),
    };
  },

  toDTO(form: FormikGreenCard): CartaVerdeDTO {
    return deleteEmptyKeys({
      numero: form.number,
      dataInizio: DateMapper.toDto(form.startDate),
      dataFine: DateMapper.toDto(form.endDate),
    });
  },
};

export const CompanyMapper = {
  fromDTO({
    companyDto,
    policyNumberDto,
    greenCardDto,
    materialDamageCoverageDto,
  }: {
    companyDto: CompagniaDTO;
    greenCardDto: CartaVerdeDTO;
    policyNumberDto: string;
    materialDamageCoverageDto?: boolean;
  }): FormikCompany {
    return {
      companyName: companyDto.denominazioneCompagnia ?? "",
      agencyName: companyDto.denominazioneAgenzia ?? "",
      impresaDb: companyDto.impresaDb,
      policyNumber: policyNumberDto,
      greenCard: GreenCardMapper.fromDTO(greenCardDto),
      materialDamageCoverage: YesNoAnswerMapper.fromDto(
        materialDamageCoverageDto
      ),
    };
  },

  toDTO(form: FormikCompany): CompagniaDTO {
    return deleteEmptyKeys({
      denominazioneCompagnia: form.companyName,
      denominazioneAgenzia: form.agencyName,
      impresaDb: form.impresaDb,
    });
  },
};

export const DriverMapper = {
  fromDTO({
    driverDto,
    driverCoincidesWithContractorDto,
  }: {
    driverDto: ConducenteDTO;
    driverCoincidesWithContractorDto?: boolean;
  }): FormikDriver {
    return {
      driverCoincidesWithContractor: YesNoAnswerMapper.fromDto(
        driverCoincidesWithContractorDto
      ),
      surname: driverDto.cognome,
      name: driverDto.nome ?? "",
      dateOfBirth: DateMapper.fromDto(driverDto.dataNascita ?? ""),
      fiscalCodeOrVAT: driverDto.codiceFiscale ?? "",
      address: driverDto.indirizzo ?? "",
      phoneNumber: {
        formattedPhone: driverDto.telefono ?? "",
      },
      email: driverDto.email ?? "",
      licenseNumber: driverDto.numeroPatente ?? "",
      category: VehicleLicenseTypesMapper.fromDTO({
        codice: driverDto.categoria ?? "",
        descrizione: driverDto.categoria ?? "",
      }),
      validUntil: driverDto.validoFinoAl ?? "",
      fullName: driverDto.nomeCompleto ?? "",
    };
  },

  toDTO(form: FormikDriver): ConducenteDTO {
    return deleteEmptyKeys({
      cognome: form.surname,
      nome: form.name,
      dataNascita: DateMapper.toDto(form.dateOfBirth),
      codiceFiscale: form.fiscalCodeOrVAT,
      indirizzo: form.address,
      telefono: form.phoneNumber.formattedPhone,
      email: form.email,
      numeroPatente: form.licenseNumber,
      categoria: form.category?.id,
      validoFinoAl: form.validUntil,
      nomeCompleto: form.fullName,
    });
  },
};

export const VehicleDamagesMapper = {
  fromDTO({
    notesDto,
    impactPointDto,
  }: {
    notesDto?: string;
    impactPointDto?: ImpactPoint[];
  }): FormikVehicleDamages {
    return {
      impactPoints: impactPointDto ?? [],
      notes: notesDto ?? "",
    };
  },
};

export const VehicleDocumentMapper = {
  fromDTO(dto: VeicoloDocumentoDTO): FormikDocument {
    return {
      documentId: dto.idDocumento,
      fileName: dto.nome,
      vehicleType: dto.tipoVeicolo,
    };
  },

  toDTO(form: FormikDocument): VeicoloDocumentoDTO {
    return deleteEmptyKeys({
      idDocumento: form.documentId,
      nome: form.fileName,
      tipoDocumento: "ALLEGATI", // Forced value
      tipoVeicolo: form.vehicleType,
    });
  },
};

export const VehicleLicenseTypesMapper = {
  fromDTO(dto: TipoPatenteDTO): InputSelectOption {
    return {
      id: dto.codice,
      value: dto.descrizione,
    };
  },

  toDTO(form: InputSelectOption): string {
    return form.id;
  },
};
